import { Box, createStyles } from '@mantine/core';
import React, { useMemo } from 'react';

import { useIsSidebarOpen } from '@portals/redux';
import { PartnerFeatureFlagsType, TenantType } from '@portals/types';

import { AuthenticationGuard } from '../components/AuthenticationGuard';
import { GlobalSubscription } from '../components/GlobalSubscription';
import { HubsNavbar } from '../components/layout/navbar/HubsNavbar';
import { Navbar } from '../components/layout/navbar/Navbar';
import { HubsSidebar } from '../components/layout/sidebar/hubs-sidebar/HubsSidebar';
import { Sidebar } from '../components/layout/sidebar/Sidebar';
import { Main } from '../components/Main';
import Onboarded from '../components/Onboarded';
import { usePermissionAccess } from '../components/permission-access/use-permission-access';
import { QuickNavProvider } from '../components/QuickNav';
import { useAppConfig } from '../context';
import { useCommonFeatureFlags } from '../hooks/portal-config';
import { useHasSupportSeat } from '../hooks/support-seats';
import { Page404 } from '../pages/auth/Page404';

interface DashboardProps {
  children: React.ReactNode;
}

export function DashboardLayout({ children }: DashboardProps) {
  const isSidebarOpen = useIsSidebarOpen();

  const { classes, cx } = useStyles(isSidebarOpen);
  const { extraLayout = {}, tenantType } = useAppConfig();
  const hasSupportSeat = useHasSupportSeat();
  const { isMissingAllPermissions, isSuperAdmin } = usePermissionAccess();
  const featureFlags = useCommonFeatureFlags();

  const {
    dashboardDataLayout: DataLayout,
    globalLayout,
    options = {},
  } = extraLayout;

  const hasNoPermissions =
    isMissingAllPermissions && !isSuperAdmin && !hasSupportSeat;

  // TODO: Hubs layout
  const isPartner = tenantType === TenantType.Partner;
  const isHubsEnabled =
    isPartner && (featureFlags as PartnerFeatureFlagsType)?.hubs === true;
  // TODO: Hubs layout

  const content = useMemo(() => {
    if (!isHubsEnabled) {
      return (
        <>
          {options.isMobile ? null : <Sidebar />}

          <Main>
            <Navbar />
            <div
              id="page-content"
              className={cx(classes.content, 'page-content')}
            >
              {hasNoPermissions ? (
                <Page404
                  hideActionButton
                  hideImage
                  title="Missing permissions"
                  description="We're sorry, it looks like you don't have the necessary permissions to access this page. Please reach out to your administrator for assistance."
                />
              ) : (
                children
              )}
            </div>

            {globalLayout ? globalLayout() : null}

            <GlobalSubscription tenantType={tenantType} />
          </Main>
        </>
      );
    }

    // TODO: Hubs layout
    return (
      <>
        <HubsNavbar />

        <Main isHubsEnabled>
          {options.isMobile ? null : <HubsSidebar />}

          <div
            id="page-content"
            className={cx(classes.content, 'page-content')}
          >
            {hasNoPermissions ? (
              <Page404
                hideActionButton
                hideImage
                title="Missing permissions"
                description="We're sorry, it looks like you don't have the necessary permissions to access this page. Please reach out to your administrator for assistance."
              />
            ) : (
              children
            )}
          </div>

          {globalLayout ? globalLayout() : null}

          <GlobalSubscription tenantType={tenantType} />
        </Main>
      </>
    );
  }, [
    children,
    classes.content,
    cx,
    globalLayout,
    hasNoPermissions,
    isHubsEnabled,
    options.isMobile,
    tenantType,
  ]);

  return (
    <AuthenticationGuard>
      <QuickNavProvider>
        <Box
          className={cx(classes.wrapper, 'page-wrapper', {
            // TODO: Hubs layout
            'hubs-enabled': isHubsEnabled,
            // TODO: Hubs layout
          })}
        >
          {DataLayout ? (
            <DataLayout>
              <Onboarded>{content}</Onboarded>
            </DataLayout>
          ) : (
            <Onboarded>{content}</Onboarded>
          )}
        </Box>
      </QuickNavProvider>
    </AuthenticationGuard>
  );
}

const useStyles = createStyles((theme, isSidebarOpen: boolean) => ({
  wrapper: {
    height: '100vh',
    overflow: 'hidden',
    display: 'grid',

    // TODO: Hubs layout (also remove the :not line when removing hubs)
    '&.hubs-enabled': {
      gridTemplateRows: `${theme.other.navbarHeight}px 1fr`,
      gridTemplateColumns: 'unset',
    },
    // TODO: Hubs layout

    '&:not(.hubs-enabled)': {
      gridTemplateColumns: `${isSidebarOpen ? 300 : 76}px 1fr`,

      [theme.fn.smallerThan('xl')]: {
        gridTemplateColumns: `${isSidebarOpen ? 250 : 76}px 1fr`,
      },
    },
  },
  content: {
    flex: 1,
    overflow: 'auto',
    position: 'relative',
  },
}));
